import { EyeOff } from 'lucide-react';
import type { InsightsKpi } from '@ucp/web/lib';
import { Card } from '../ui/Card';
import { ChartWithTable } from './charts/ChartWithTable';
import { ProvenanceBadge } from './ProvenanceBadge';
import { formatKpiValue } from './format';

/**
 * A single KPI tile (PRO-0590, ADR-038). Mirrors the `ComplaintKpiPanel` token
 * `Card` style: a label, a headline value, the mandatory provenance badge for
 * non-live data, and an accessible chart+table trend (NF-10). Presentational only
 * — all values arrive pre-aggregated and PII-safe from the `InsightsSource`.
 *
 * Small-number suppression (PRO-0590 Increment 2, ADR-038 A1-4 / TD-5): a suppressed
 * KPI arrives whole-KPI-suppressed (`value:null`, `suppressed:true`, `trend:[]`). It
 * renders the shared DS empty-state pattern (DS-4/DS-5 — an informative `role="status"`
 * note, not a dead end, and NOT colour-only: an icon + text equivalent, NF-10) in place
 * of the headline value + chart. No value or cohort size is ever shown.
 */
export function KpiTile({
  kpi,
  dataTableOpen = true,
}: {
  kpi: InsightsKpi;
  /**
   * Open/closed state of the chart data-table disclosure. The table always stays
   * in the DOM (NF-10). Defaults to open so callers without a preference match
   * the shipped behaviour.
   */
  dataTableOpen?: boolean;
}) {
  const suppressed = kpi.suppressed === true || kpi.value === null;
  return (
    <li>
      <Card className="p-4" aria-label={kpi.label}>
        <div className="flex flex-wrap items-start justify-between gap-2">
          <p className="m-0 text-sm text-text-secondary">{kpi.label}</p>
          <ProvenanceBadge isLive={kpi.isLive} />
        </div>
        {suppressed ? (
          <div
            role="status"
            className="mt-2 flex items-center gap-2 rounded-md bg-muted p-3 text-sm text-text-secondary"
          >
            <EyeOff aria-hidden="true" className="h-4 w-4 shrink-0" />
            <span>Not shown — cohort too small to report without risk of identification.</span>
          </div>
        ) : (
          <>
            <p className="m-0 mt-1 text-2xl font-bold text-text-primary">
              {formatKpiValue(kpi.value ?? 0, kpi.kind)}
            </p>
            <div className="mt-3">
              <ChartWithTable
                title={kpi.label}
                chart={kpi.chart}
                kind={kpi.kind}
                unit={kpi.unit}
                trend={kpi.trend}
                dataTableOpen={dataTableOpen}
              />
            </div>
          </>
        )}
      </Card>
    </li>
  );
}
